import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private permissionService = inject(PermissionService);
  private router = inject(Router);
  
  // Flag to prevent multiple redirects - use sessionStorage for persistence across page reloads
  private get isRedirecting(): boolean {
    return sessionStorage.getItem('auth_redirecting') === 'true';
  }
  
  private set isRedirecting(value: boolean) {
    if (value) {
      sessionStorage.setItem('auth_redirecting', 'true');
    } else {
      sessionStorage.removeItem('auth_redirecting');
    }
  }

  constructor() {
    // Debug: Log when interceptor is instantiated
    console.log('[AuthInterceptor] Interceptor instantiated');
  }

  /**
   * Get the base href from the DOM or default to '/'
   * This handles apps deployed with different base hrefs (e.g., /accounting/, /demo/)
   */
  private getBaseHref(): string {
    const baseTag = document.querySelector('base');
    if (baseTag && baseTag.getAttribute('href')) {
      const baseHref = baseTag.getAttribute('href') || '/';
      // Ensure it ends with / and remove trailing slash for path construction
      return baseHref.endsWith('/') ? baseHref.slice(0, -1) : baseHref;
    }
    return '';
  }

  /**
   * Clear access_token cookie with domain from URL
   */
  private clearAllCookies(): void {
    const expiredDate = 'Thu, 01 Jan 1970 00:00:00 UTC';
    const hostname = window.location.hostname;
    const domain = hostname.includes('.') ? `.${hostname.split('.').slice(-2).join('.')}` : hostname;

    // Clear access_token cookie with domain
    document.cookie = `access_token=; expires=${expiredDate}; path=/; domain=${domain};`;
    document.cookie = `access_token=; expires=${expiredDate}; path=/; domain=${domain}; SameSite=None; Secure;`;
    
    // Also try without domain
    document.cookie = `access_token=; expires=${expiredDate}; path=/;`;
    document.cookie = `access_token=; expires=${expiredDate}; path=/; SameSite=None; Secure;`;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip interceptor for /auth/authenticate endpoint
    // This endpoint is used to check if user is already logged in
    // We don't want to redirect on 401 for this endpoint
    if (req.url.includes('/auth/authenticate')) {
      return next.handle(req.clone({ withCredentials: true }));
    }
    
    // Skip interceptor for login/auth endpoints to prevent redirect loops
    if (req.url.includes('/auth/login') || req.url.includes('/auth/register')) {
      return next.handle(req.clone({ withCredentials: true }));
    }
    
    // If we're already redirecting, skip processing to prevent loops
    if (this.isRedirecting) {
      return next.handle(req.clone({ withCredentials: true }));
    }

    // Debug: Log that interceptor is active
    // if (req.url.includes('/api/')) {
    //   console.log('[AuthInterceptor] Intercepting request:', req.method, req.url);
    // }

    const clonedReq = req.clone({
      withCredentials: true,
    });

    return next.handle(clonedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Debug: Log all errors to see if interceptor is catching them
        if (error.status) {
          console.log('[AuthInterceptor] Error caught - Status:', error.status, 'URL:', error.url);
        }

        // Handle authentication/authorization errors
        if (error.status === 401) {
          // Prevent multiple redirects
          if (this.isRedirecting) {
            console.log('[AuthInterceptor] Already redirecting, skipping duplicate redirect');
            return throwError(() => error);
          }
          
          // Check if we're already on the login page
          const currentPath = window.location.pathname;
          if (currentPath.includes('/login')) {
            console.log('[AuthInterceptor] Already on login page, skipping redirect');
            return throwError(() => error);
          }
          
          // Skip redirect for login/auth endpoints
          if (error.url && (error.url.includes('/auth/login') || error.url.includes('/auth/register'))) {
            console.log('[AuthInterceptor] Login/auth endpoint error, skipping redirect');
            return throwError(() => error);
          }
          
          console.error('[AuthInterceptor] 401 Unauthorized detected');
          console.error('[AuthInterceptor] Request URL:', error.url);
          console.error('[AuthInterceptor] Error details:', error);
          
          // Set redirect flag to prevent multiple redirects
          this.isRedirecting = true;
          
          // Clear all cookies
          this.clearAllCookies();
          console.log('[AuthInterceptor] Cookies cleared');
          
          // Clear any stored auth data
          this.authService.logout();
          console.log('[AuthInterceptor] Auth service logged out');
          
          // Clear permission service tokens
          this.permissionService.clearAuthToken();
          console.log('[AuthInterceptor] Permission tokens cleared');
          
          // Get base href to support apps deployed with different base paths (e.g., /accounting/, /demo/)
          const baseHref = this.getBaseHref();
          const loginPath = `${baseHref}/login`;
          
          console.log('[AuthInterceptor] Base href:', baseHref);
          console.log('[AuthInterceptor] Redirecting to:', loginPath);
          console.log('[AuthInterceptor] Current pathname:', window.location.pathname);
          
          // Use window.location for hard redirect to ensure navigation happens
          // This ensures the page fully reloads and clears any cached state
          // Clear the redirect flag after redirect (it will be cleared on next page load)
          window.location.href = loginPath;
          
          // Return error immediately to prevent further processing
          return throwError(() => error);
        } else if (error.status === 403) {
          console.warn('[AuthInterceptor] 403 Forbidden - Redirecting to unauthorized page');
          console.warn('[AuthInterceptor] Request URL:', error.url);
          
          // Get base href to support apps deployed with different base paths
          const baseHref = this.getBaseHref();
          const unauthorizedPath = `${baseHref}/unauthorized`;
          
          // Redirect to unauthorized page with correct path
          this.router.navigateByUrl(unauthorizedPath);
        }
        
        return throwError(() => error);
      })
    );
  }
}
